import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const ComingSoon = () => {
  const navigate = useNavigate();

  const handleBackHome = () => {
    navigate('/'); // Navigate to home page (Hero component)
  };

  const handleEarlyAccess = () => {
    navigate('/waitlist'); // Navigate to waitlist, aligning with Hero
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated background elements - pink, purple, cyan */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-10 left-10 w-72 h-72 bg-pink-400/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/3 right-20 w-96 h-96 bg-purple-400/15 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, 50, 0],
            y: [0, -80, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 left-1/4 w-80 h-80 bg-cyan-300/20 rounded-full blur-3xl"
        />
        
        {/* Decorative geometric shapes */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 right-1/3 w-32 h-32 border-2 border-pink-300/30 rounded-lg"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/3 left-1/4 w-24 h-24 border-2 border-cyan-300/30"
          style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
        />
      </div>

      {/* Main content */}
      {/* Main content */}
<div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12 pt-20"> {/* Added pt-20 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl"
        >
          {/* Creative logo with orbit effect */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 150 }}
            className="flex justify-center mb-12 relative"
          >
            <div className="relative">
              {/* Orbiting dots */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 w-32 h-32"
              >
                <div className="absolute top-0 left-1/2 w-2 h-2 bg-pink-500 rounded-full -ml-1" />
              </motion.div>
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 w-32 h-32"
              >
                <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-cyan-500 rounded-full -ml-1" />
              </motion.div>
              
              <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-purple-400 to-cyan-500 rounded-3xl blur-xl opacity-30" />
              <div className="relative w-24 h-24 bg-gradient-to-br from-pink-500 via-purple-500 to-cyan-500 rounded-3xl flex items-center justify-center shadow-2xl">
                <svg className="w-14 h-14 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </motion.div>

          {/* Main heading with creative typography */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-7xl md:text-8xl lg:text-9xl font-black mb-2 tracking-tight">
              <span className="inline-block bg-gradient-to-r from-slate-700 to-slate-500 bg-clip-text text-transparent">
                Coming
              </span>
            </h1>
            <h1 className="text-7xl md:text-8xl lg:text-9xl font-black tracking-tight">
              <span className="inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                Soon
              </span>
            </h1>
          </motion.div>

          {/* Creative subheading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-6"
          >
            <p className="text-2xl md:text-3xl font-bold text-slate-700 mb-3">
              Something extraordinary is brewing
            </p>
            <div className="flex justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 0.8 + i * 0.1, duration: 0.5 }}
                  className="h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 rounded-full"
                  style={{ maxWidth: `${(5 - i) * 20}px` }}
                />
              ))}
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="text-lg md:text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            We're crafting an intelligent platform that thinks differently. 
            <span className="font-semibold text-slate-700"> ZuriIntel</span> is redefining what's possible with AI technology.
          </motion.p>

          {/* Creative progress indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mb-12"
          >
            <div className="flex justify-center items-center gap-3">
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleY: 0.3 }}
                    animate={{ scaleY: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.15,
                    }}
                    className="w-1.5 bg-gradient-to-t from-pink-500 via-purple-500 to-cyan-500 rounded-full"
                    style={{ height: '32px' }}
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-slate-500 ml-2">Building magic...</span>
            </div>
          </motion.div>

          {/* Back to Home button with unique style */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleBackHome}
            className="group relative px-10 py-4 text-base font-bold text-white rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
            <span className="relative flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return Home
            </span>
          </motion.button>

          {/* Creative footer section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mt-20 pt-10 border-t border-slate-200"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium">In Development</span>
              </div>
              <span className="hidden md:block text-slate-300">•</span>
              <button
                onClick={handleEarlyAccess}
                className="text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors flex items-center gap-2"
              >
                <span>Get early access</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Brand signature */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
        className="relative z-10 py-8 text-center"
      >
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm text-slate-400">Powered by</span>
          <span className="text-lg font-black bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
            ZuriIntel
          </span>
          <span className="text-xs text-purple-600 font-semibold px-2 py-0.5 bg-purple-50 rounded-full">AI</span>
        </div>
      </motion.footer>
    </div>
  );
};

export default ComingSoon;